import { useState } from "react";
import { updatePassword } from "firebase/auth";
import { doc, updateDoc } from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import { useAuth } from "../context/AuthContext";
import { LockKey, Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";

// Shown when users/{uid}.mustChangePassword is true — the client logged in
// with a coach-generated temp password and must set their own before entering
// the app. Clearing the flag flips the profile listener and routes them in.
export default function ForcePasswordChange() {
  const { user, signOut } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      await updatePassword(auth.currentUser, pw);
      await updateDoc(doc(db, "users", user.uid), { mustChangePassword: false });
      toast.success("Password set — welcome to Seshly!");
    } catch (err) {
      if (err.code === "auth/requires-recent-login") {
        toast.error("Session expired — please log in again with your temporary password");
        signOut();
      } else {
        toast.error(err?.message || "Couldn't set password");
      }
      setBusy(false);
    }
  }

  return (
    <div className="min-h-dvh bg-white flex flex-col items-center justify-center px-6">
      <div style={{ fontFamily: "Inter, Arial, sans-serif", fontSize: "36px", fontWeight: "800", color: "#10B981", letterSpacing: "0.5px", marginBottom: "28px" }}>
        seshly.
      </div>
      <div className="w-full max-w-xs text-center">
        <div className="w-14 h-14 rounded-2xl bg-[var(--brand)]/10 flex items-center justify-center mx-auto mb-4">
          <LockKey size={26} weight="duotone" className="text-[var(--brand)]" />
        </div>
        <h2 className="text-2xl font-bold text-[#1a2b3b]">Set your password</h2>
        <p className="text-sm text-neutral-500 mt-2 mb-7 leading-relaxed">
          You logged in with a temporary password. Choose your own to secure your account.
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-3 text-left">
          <div className="relative">
            <input
              type={show ? "text" : "password"}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="New password (min 8 characters)"
              autoFocus
              className="w-full h-12 rounded-2xl border border-black/10 px-4 pr-11 text-sm text-[#1a2b3b] focus:outline-none focus:ring-2 focus:ring-[var(--brand)]/30 focus:border-[var(--brand)]/40"
              data-testid="new-password-input"
            />
            <button
              type="button"
              onClick={() => setShow((v) => !v)}
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-neutral-300 hover:text-neutral-500"
              tabIndex={-1}
            >
              {show ? <EyeSlash size={17} /> : <Eye size={17} />}
            </button>
          </div>
          <input
            type={show ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="Confirm password"
            className="w-full h-12 rounded-2xl border border-black/10 px-4 text-sm text-[#1a2b3b] focus:outline-none focus:ring-2 focus:ring-[var(--brand)]/30 focus:border-[var(--brand)]/40"
            data-testid="confirm-password-input"
          />
          {confirm.length > 0 && pw !== confirm && (
            <div className="text-[11px] text-amber-500 font-medium pl-1">Passwords don't match yet</div>
          )}
          <button
            type="submit"
            disabled={busy || pw.length < 8 || pw !== confirm}
            className="h-12 mt-1 rounded-2xl bg-[var(--brand)] text-white font-bold text-sm tracking-wide shadow-lg shadow-[var(--brand)]/25 disabled:opacity-40 disabled:shadow-none active:scale-[0.98] transition-all"
            data-testid="set-password-btn"
          >
            {busy ? "Setting up…" : "Continue to Dashboard"}
          </button>
        </form>

        <button
          onClick={signOut}
          className="text-xs text-neutral-400 underline underline-offset-2 hover:text-neutral-600 mt-6"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}
